// src/utils/risk/index.ts
export interface RiskFlag {
  category: 'suicide' | 'depression' | 'abuse' | 'crisis_general';
  link: string;
}

export const HELP_LINK =
  'https://findahelpline.com/?utm_source=chatgpt.com&utm_placement=banner';

const patterns: Record<RiskFlag['category'], RegExp[]> = {
  suicide: [
    /\bkill(?:\s|-)my(?:\s|-)self\b/i,
    /\bend(?:\s|-)my(?:\s|-)life\b/i,
    /\bsuicid(?:e|al)\b/i,
    /\btake(?:\s|-)my(?:\s|-)own(?:\s|-)life\b/i,
    /\bwant(?:\s|-)to(?:\s|-)die\b/i,
    /\b(can'?t|cannot)\s+go\s+on\s+living\b/i,
    /\bending\s+it\s+all\b/i,
    /\bbetter\s+off\s+dead\b/i,
    /\b(overdose|hang\s+myself|jump\s+off)\b/i,
    /\b(farewell|final)\s+goodbye\b/i,
  ],
  depression: [
    /\bworthless(?:ness)?\b/i,                 // worthless | worthlessness
    /\b(i\s+feel\s+)?hopeless(ness)?\b/i,
    /\b(i\s+am\s+)?useless\b/i,
    /\bi\s*(do\s*not|don't)\s*matter\b/i,
    /\b(i'?m|i\s+am)\s+so\s+tired\s+of\s+life\b/i,
    /\bnothing\s+will\s+ever\s+get\s+better\b/i,
    /\b(i\s+want\s+to\s+)?disappear\b/i,
    /\blife\s+has\s+no\s+meaning\b/i,
    /\bi\s+can'?t\s+do\s+this\s+anymore\b/i,
    /\bi\s+feel\s+like\s+a\s+burden\b/i,
    /\bnobody\s+cares\s+about\s+me\b/i,
    /\bdark\s+thoughts?\b/i,
  ],
  abuse: [
    /\b(domestic\s+violence|abusive\s+relationship)\b/i,
    /\bpartner\s+(hits|hurts|beats)\s+me\b/i,
    /\b(i'?m|i\s+am)\s+not\s+safe\s+at\s+home\b/i,
    /\bthey\s+hit\s+me\b/i,
    /\bsexual\s+assault\b/i,
    /\b(molest(ed|ation)?|raped?)\b/i,
    /\bthreaten(ed)?\s+with\s+violence\b/i,
    /\btrapped\s+with\s+abuser\b/i,
    /\b(can'?t|cannot)\s+escape\s+abuse\b/i,
  ],
  crisis_general: [
    /\bi\s+want\s+to\s+die\b/i,
    /\bi'?m\s+not\s+safe\b/i,
    /\b(in\s+)?danger\b/i,
    /\bfeel\s+trapped\b/i,
    /\bneed\s+help\s+now\b/i,
    /\bi\s+can'?t\s+take\s+this\b/i,
    /\blosing\s+control\b/i,
    /\bat\s+the\s+end\s+of\s+my\s+rope\b/i,
    /\bi\s+need\s+someone\b/i,
    /\bi'?m\s+scared\b/i,
    /\bpanic\s+attack\b/i,
    /\blife\s+is\s+over\b/i,
  ],
};

function toScanText(input: unknown): string {
  if (!input) return '';
  if (typeof input === 'string') return input;
  try { return JSON.stringify(input); } catch { return String(input); }
}

export function detectRiskFlag(textOrObject: unknown): RiskFlag | null {
  const haystack = toScanText(textOrObject);
  if (!haystack) return null;

  for (const [category, regs] of Object.entries(patterns) as [
    RiskFlag['category'], RegExp[]
  ][]) {
    if (regs.some((rx) => rx.test(haystack))) {
      if (import.meta.env.DEV) {
        console.info('[risk] match:', category, { sample: haystack.slice(0, 140) });
      }
      return { category, link: HELP_LINK };
    }
  }
  if (import.meta.env.DEV) {
    console.info('[risk] no-match');
  }
  return null;
}

// Dev-only console helper: window.__risk('text')
declare global { interface Window { __risk?: (s: string) => RiskFlag | null } }
if (import.meta.env.DEV) {
  window.__risk = (s: string) => detectRiskFlag(s);
}
